/**
 * AnRa Variant Image Filter (JS-only, no Liquid template edits required)
 *
 * Anett (Meeting 04.05.): "Wenn ich auf eine Farbe klicke, will ich nur die
 * Bilder dieser Variante sehen, nicht alle Hosen auf einmal."
 *
 * Strategy:
 *  1. On load, scan every gallery slide and detect a color keyword from the
 *     image's alt-text and filename. Persist the result on the slide as a
 *     `data-anra-color` attribute (comma-separated when multiple colors
 *     appear, e.g. "tuerkis,smaragd" for combined-look shots).
 *  2. Resolve the currently selected color (URL `variant` param → selected
 *     radio/select input → first variant). Filter the gallery accordingly.
 *  3. Listen for Ritual's `variant:selected` / `variant:update` events plus
 *     direct change events on color inputs. Re-filter on every selection.
 *
 * Slides with no detectable color stay visible (generic detail / atmosphere
 * shots Anett wants to keep regardless of variant).
 */
(() => {
  if (typeof window === 'undefined') return;

  const HIDE_CLASS = 'anra-color-hide';
  const COLOR_ATTR = 'data-anra-color';

  // Color synonym map. Keys are the canonical color, values are the keywords
  // that should resolve to that key. Detection is case-insensitive and umlaut-
  // tolerant (`normalize()` strips umlauts before comparison).
  const COLOR_KEYWORDS = {
    pink: ['pink', 'magenta', 'fuchsia', 'mohnblume'],
    rot: ['rot', 'red', 'carmine', 'rubin', 'kirsch'],
    tuerkis: ['tuerkis', 'türkis', 'turkis'],
    aqua: ['aqua'],
    blau: ['blau', 'blue', 'denim', 'jeans', 'navy', 'marine', 'kobalt'],
    smaragd: ['smaragd', 'emerald', 'jade'],
    gruen: ['gruen', 'grün', 'green', 'oliv', 'khaki', 'tarn'],
    weiss: ['weiss', 'weiß', 'white', 'creme', 'ecru', 'natur'],
    schwarz: ['schwarz', 'black', 'noir'],
    grau: ['grau', 'grey', 'gray', 'anthrazit', 'silber', 'silver'],
    beige: ['beige', 'sand', 'taupe'],
    braun: ['braun', 'brown', 'cognac'],
    lila: ['lila', 'violett', 'purple', 'flieder', 'aubergine'],
    orange: ['orange', 'lachs', 'salmon', 'apricot', 'koralle'],
    gelb: ['gelb', 'yellow', 'senf', 'gold'],
    rosa: ['rosa', 'altrosa', 'puder'],
  };

  // Related colors map: when a variant is e.g. "Smaragd-dora" but Anett has
  // catalogued the matching photos under "tuerkis" filenames (real data
  // inconsistency), include the related colors so the customer still sees
  // the correct shots. Symmetric — both keys exist on each side.
  const RELATED_COLORS = {
    // AnRa-spezifisch: Anett katalogisiert Smaragd-Varianten oft mit
    // "tuerkis"-Filenames. Symmetrisch mappen.
    tuerkis: ['smaragd'],
    smaragd: ['tuerkis'],
    // Allgemeine Synonyme — nur wenn die Farbe bei AnRa typischerweise
    // austauschbar gepflegt ist. Aqua bleibt isoliert (eigene Variante).
    weiss: ['creme', 'natur', 'ecru'],
    schwarz: ['anthrazit'],
    pink: ['magenta', 'fuchsia'],
    rot: ['kirsch', 'rubin'],
    blau: ['marine', 'navy'],
    beige: ['sand'],
    grau: ['silber'],
  };

  // Build a flat lookup of normalized keyword -> canonical color.
  const KEYWORD_TO_COLOR = (() => {
    const m = new Map();
    for (const [color, keywords] of Object.entries(COLOR_KEYWORDS)) {
      for (const kw of keywords) m.set(normalize(kw), color);
    }
    return m;
  })();

  function normalize(s) {
    if (!s) return '';
    return String(s)
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ');
  }

  // Detect every color keyword present in `text`. Returns array of canonical
  // colors (deduped) — empty if nothing matched.
  function detectColors(text) {
    if (!text) return [];
    const norm = normalize(text);
    const found = new Set();
    for (const [kw, color] of KEYWORD_TO_COLOR.entries()) {
      // Word-boundary-ish match: surrounded by space-or-edge to avoid
      // matching "rot" inside "rotate" etc. Norm replaces non-alphanum with
      // space so we can rely on space boundaries.
      if (norm.includes(' ' + kw + ' ') || norm.startsWith(kw + ' ') ||
          norm.endsWith(' ' + kw) || norm === kw) {
        found.add(color);
      }
    }
    return Array.from(found);
  }

  function getSlides() {
    // Hauptgalerie auf der Produktseite + Lightbox-Slides + Thumbnail-Buttons
    // im zoom-dialog. Alle drei rendern dieselbe Bild-Liste, alle drei müssen
    // synchron gefiltert werden — sonst sieht der Kunde in der Lightbox oder
    // in der Thumbnail-Leiste Bilder anderer Farben.
    return document.querySelectorAll(
      '.product-information__media .product-media, ' +
      'zoom-dialog .product-media, ' +
      'zoom-dialog .dialog-thumbnails-list__thumbnail'
    );
  }

  // Tag every slide with its detected colors. Idempotent — re-running just
  // re-writes the attribute.
  function tagSlides() {
    getSlides().forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const alt = img.getAttribute('alt') || '';
      const src = (img.getAttribute('src') || img.currentSrc || '')
        .split('/')
        .pop()
        .split('?')[0];
      const colors = detectColors(alt + ' ' + src);
      if (colors.length) {
        slide.setAttribute(COLOR_ATTR, colors.join(','));
      } else {
        slide.removeAttribute(COLOR_ATTR);
      }
    });
  }

  // Filter slides: keep slides whose detected colors intersect with the
  // target color set (primary + related). Slides without `data-anra-color`
  // are treated as generic shots and stay visible. If filtering would hide
  // every single slide (data inconsistency case), fall back to showing all.
  function filterByColor(targetColorRaw) {
    const targetColor = normalizeVariantColor(targetColorRaw);
    const slides = getSlides();
    if (!targetColor) {
      slides.forEach((s) => s.classList.remove(HIDE_CLASS));
      return;
    }
    const allowed = new Set([targetColor]);
    (RELATED_COLORS[targetColor] || []).forEach((c) => allowed.add(c));

    let taggedAndMatched = 0;
    slides.forEach((slide) => {
      const attr = slide.getAttribute(COLOR_ATTR);
      if (!attr) {
        // Generic shot — keep visible
        slide.classList.remove(HIDE_CLASS);
        return;
      }
      const colors = attr.split(',');
      if (colors.some((c) => allowed.has(c))) {
        slide.classList.remove(HIDE_CLASS);
        taggedAndMatched++;
      } else {
        slide.classList.add(HIDE_CLASS);
      }
    });

    // Safety: if zero color-tagged slides matched (e.g. variant data is
    // entirely inconsistent with image alt-texts), show everything rather
    // than leave the customer with an empty gallery.
    if (taggedAndMatched === 0) {
      slides.forEach((s) => s.classList.remove(HIDE_CLASS));
    }
  }

  // Anett (Meeting Z. 2018): "wenn man auf einen Color-Button klickt, da muss
  // das Foto oben sein." Smooth-Scroll zum Media-Container — das ist der
  // sichtbare Galerie-Block der gesamten Produktseite (nicht ein einzelner
  // Slide, der versehentlich auf einem versteckten Mobile-Carousel sitzt).
  function scrollToFirstVisibleImage() {
    const mediaContainer = document.querySelector(
      '.product-information__media'
    );
    if (!mediaContainer) return;
    const rect = mediaContainer.getBoundingClientRect();
    // Only scroll if the container is currently scrolled significantly
    // out of the top half of the viewport
    if (rect.top < -100 || rect.top > window.innerHeight * 0.4) {
      // Scroll to a position that places the media container near the top
      // of the viewport, with some breathing room for the sticky header.
      const targetY = window.scrollY + rect.top - 100;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }
  }

  // Variant option values come as "Pink-dora", "Türkis-dora", "Blau-dora", etc.
  // We strip the pattern suffix and resolve the leading color to a canonical key.
  function normalizeVariantColor(raw) {
    if (!raw) return null;
    // First token before "-" or " " is usually the pure color name.
    const first = String(raw).split(/[\s\-_/]+/)[0];
    const colors = detectColors(first) ;
    if (colors.length) return colors[0];
    // Fallback: detect anything in the full string
    const all = detectColors(raw);
    return all.length ? all[0] : null;
  }

  function getCurrentVariantColor() {
    // 1. Selected color radio / checked input
    const colorInput = document.querySelector(
      'input[type="radio"][name*="Color" i]:checked, input[type="radio"][name*="Farbe" i]:checked, input[type="radio"][name*="color" i]:checked'
    );
    if (colorInput && colorInput.value) return colorInput.value;
    // 2. Selected option in a select
    const colorSelect = document.querySelector(
      'select[name*="Color" i], select[name*="Farbe" i]'
    );
    if (colorSelect && colorSelect.value) return colorSelect.value;
    // 3. Variant input value combined with product JSON
    try {
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variantInput.value && window.ShopifyAnalytics) {
        // Best effort: don't block on this
      }
    } catch (e) { /* noop */ }
    return null;
  }

  function applyInitialFilter() {
    tagSlides();
    const color = getCurrentVariantColor();
    filterByColor(color);
  }

  function handleVariantSelected(event) {
    const variant = event && event.detail && event.detail.variant;
    if (variant) {
      // Look at variant.options or variant.option1/2/3
      let color = null;
      if (Array.isArray(variant.options)) {
        for (const opt of variant.options) {
          if (typeof opt === 'string') {
            const c = normalizeVariantColor(opt);
            if (c) { color = opt; break; }
          } else if (opt && opt.name && /color|farbe/i.test(opt.name)) {
            color = opt.value; break;
          }
        }
      }
      if (!color) color = variant.option1 || variant.option2 || variant.option3;
      filterByColor(color);
      setTimeout(scrollToFirstVisibleImage, 80);
      return;
    }
    // Fallback if event payload missing — read from DOM
    filterByColor(getCurrentVariantColor());
    setTimeout(scrollToFirstVisibleImage, 80);
  }

  function handleInputChange(e) {
    const t = e.target;
    if (!t) return;
    const name = (t.getAttribute('name') || '').toLowerCase();
    if (!/color|farbe/.test(name)) return;
    if (t.checked || t.tagName === 'SELECT') {
      filterByColor(t.value);
      // Defer scroll one tick so the filter has updated visibility
      setTimeout(scrollToFirstVisibleImage, 80);
    }
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyInitialFilter);
  } else {
    applyInitialFilter();
  }

  // Re-tag + re-filter whenever Ritual swaps gallery DOM (e.g. via section
  // re-render on variant change). MutationObserver on the media container.
  const containerForObserver = document.querySelector(
    '.product-information__media'
  );
  if (containerForObserver) {
    const obs = new MutationObserver(() => {
      // Coalesce re-runs to avoid thrashing
      clearTimeout(window.__anraColorFilterTimer);
      window.__anraColorFilterTimer = setTimeout(applyInitialFilter, 50);
    });
    obs.observe(containerForObserver, { childList: true, subtree: true });
  }

  // Event listeners
  document.addEventListener('variant:selected', handleVariantSelected);
  document.addEventListener('variant:update', handleVariantSelected);
  document.addEventListener('change', handleInputChange);

  // Expose for debugging
  window.__anraColorFilter = {
    tagSlides,
    filterByColor,
    getCurrentVariantColor,
    detectColors,
    normalizeVariantColor,
  };
})();
